/**
 * ExpandableEditableItem - Expandable row with inline editable value
 * Combines expand icon, editable label/value, and collapsible content
 */

import React, { useState, useCallback } from 'react';
import { HStack, Input, IconButton, Spinner, Tooltip } from '@chakra-ui/react';
import { Pencil, Check, X, RotateCcw } from 'lucide-react';
import { ExpandIcon } from '../ExpandIcon';
import { useEditableField, UseEditableFieldOptions } from '../../../../../hooks/useEditableField';
import './ExpandableEditableItem.css';

export interface ExpandableEditableItemProps extends Omit<UseEditableFieldOptions, 'chartId'> {
  /** Chart ID */
  chartId: string;
  /** Label text (e.g., "Place") */
  label: string;
  /** Disable editing */
  disabled?: boolean;
  /** Content to show when expanded */
  expandedContent?: React.ReactNode;
  /** ARIA controls ID */
  ariaControlsId?: string;
}

export const ExpandableEditableItem: React.FC<ExpandableEditableItemProps> = ({
  chartId,
  label,
  disabled = false,
  expandedContent,
  ariaControlsId,
  ...hookOptions
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const {
    value,
    isEditing,
    editValue,
    hasBackup,
    originalValue,
    startEdit,
    setEditValue,
    save,
    cancel,
    restore,
    isLoading
  } = useEditableField({ chartId, ...hookOptions });

  const handleToggle = useCallback(() => {
    if (expandedContent) {
      setIsExpanded(prev => !prev);
    }
  }, [expandedContent]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      save();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  const hasExpander = !!expandedContent;

  return (
    <div className="expandable-editable-container">
      <div className="expandable-editable-row">
        {/* Expand icon - only if has expandable content */}
        {hasExpander && (
          <span
            className="expandable-editable-toggle"
            onClick={handleToggle}
            role="button"
            tabIndex={0}
            aria-expanded={isExpanded}
            aria-controls={ariaControlsId}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleToggle();
              }
            }}
          >
            <ExpandIcon isExpanded={isExpanded} />
          </span>
        )}

        {/* Main content area */}
        <div className="expandable-editable-content">
          {isEditing ? (
            /* Edit mode - show input */
            <HStack spacing={1} flex={1} className="expandable-editable-input-group">
              <span className="expandable-editable-label">{label}:</span>
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                onKeyDown={handleKeyDown}
                size="xs"
                variant="flushed"
                autoFocus
                isDisabled={isLoading}
                className="expandable-editable-input"
              />
              <IconButton
                aria-label="Save"
                icon={isLoading ? <Spinner size="xs" /> : <Check size={12} />}
                size="xs"
                variant="ghost"
                colorScheme="green"
                onClick={save}
                isDisabled={isLoading}
              />
              <IconButton
                aria-label="Cancel"
                icon={<X size={12} />}
                size="xs"
                variant="ghost"
                colorScheme="red"
                onClick={cancel}
                isDisabled={isLoading}
              />
            </HStack>
          ) : (
            /* View mode - show label + value + actions */
            <>
              <span
                className="expandable-editable-label-value"
                onClick={hasExpander ? handleToggle : undefined}
                style={{ cursor: hasExpander ? 'pointer' : 'default' }}
              >
                {label}: <span className="expandable-editable-value">{value}</span>
              </span>

              {!disabled && (
                <HStack spacing={0} className="expandable-editable-actions">
                  <IconButton
                    aria-label="Edit"
                    icon={<Pencil size={12} />}
                    size="xs"
                    variant="ghost"
                    onClick={startEdit}
                  />
                  {hasBackup && (
                    <Tooltip
                      label={`Restore: ${originalValue}`}
                      placement="top"
                      hasArrow
                      fontSize="xs"
                    >
                      <IconButton
                        aria-label={`Restore original: ${originalValue}`}
                        icon={isLoading ? <Spinner size="xs" /> : <RotateCcw size={12} />}
                        size="xs"
                        variant="ghost"
                        colorScheme="orange"
                        onClick={restore}
                        isDisabled={isLoading}
                      />
                    </Tooltip>
                  )}
                </HStack>
              )}
            </>
          )}
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && expandedContent && (
        <div id={ariaControlsId} className="expandable-editable-subsection">
          {expandedContent}
        </div>
      )}
    </div>
  );
};

export default ExpandableEditableItem;
